// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";

// services
import { store } from "services/state";
import { updateAllUserData } from "services/api-catalog";
import {
  initApiGatewayClient,
  apiGatewayClient,
  cognitoDomain,
  cognitoIdentityPoolId,
  cognitoUserPoolId,
  cognitoClientId,
  cognitoRegion,
} from "services/api";
import { jwtDecode } from "jwt-decode";

const environmentName = process.env.REACT_APP_ENVIRONMENT_NAME;

export function isAuthenticated() {
  return store.idToken;
}

function getPreferredUserGroup() {
  return jwtDecode(store.idToken)["cognito:groups"] || "";
}

function isUserinGroup(groupName) {
  if (!store.idToken) {
    return false;
  }
  const group = getPreferredUserGroup();
  return group.includes(groupName);
}

export function isRegistered() {
  return (
    isUserinGroup(`${environmentName}-eem-apiportalstackAdminsGroup`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-RegisteredGroup`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-RestAPIOnlyUsers`) ||
    isUserinGroup(
      `${environmentName}-eem-apiportalstack-WebsocketAPIOnlyUsers`
    ) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-NonIkanoUsers`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-IkanoUsers`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-DeveloperToolsUsers`)
  );
}

export function isAdmin() {
  return (
    store.idToken &&
    getPreferredUserGroup().includes(
      `${environmentName}-eem-apiportalstackAdminsGroup`
    )
  );
}

export function isRestAPIUser() {
  return (
    isUserinGroup(`${environmentName}-eem-apiportalstackAdminsGroup`) ||
    isUserinGroup(
      `${environmentName}-eem-apiportalstack-DeveloperToolsUsers`
    ) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-IkanoUsers`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-NonIkanoUsers`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-RestAPIOnlyUsers`)
  );
}

export function isWsAPIUser() {
  return (
    isUserinGroup(`${environmentName}-eem-apiportalstackAdminsGroup`) ||
    isUserinGroup(
      `${environmentName}-eem-apiportalstack-DeveloperToolsUsers`
    ) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-IkanoUsers`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-NonIkanoUsers`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-WebsocketAPIOnlyUsers`)
  );
}

export function isNonIkanoUser() {
  return (
    isUserinGroup(`${environmentName}-eem-apiportalstackAdminsGroup`) ||
    isUserinGroup(
      `${environmentName}-eem-apiportalstack-DeveloperToolsUsers`
    ) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-NonIkanoUsers`)
  );
}

export function isIkanoUser() {
  return (
    isUserinGroup(`${environmentName}-eem-apiportalstackAdminsGroup`) ||
    isUserinGroup(
      `${environmentName}-eem-apiportalstack-DeveloperToolsUsers`
    ) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-IkanoUsers`)
  );
}

export function isDeveloperToolsUser() {
  return (
    isUserinGroup(`${environmentName}-eem-apiportalstackAdminsGroup`) ||
    isUserinGroup(`${environmentName}-eem-apiportalstack-DeveloperToolsUsers`)
  ) && store?.user?.validWorkspace === true;
}

let logoutTimer;

function getRemainingSessionTime(idToken) {
  return jwtDecode(idToken).exp * 1000 - Date.now();
}

/**
 * On page load, look for an active cookie. If it exists and isn't expired, great, use it. Otherwise, clear everything and make sure we're not logged in.
 */
export function init() {
  // attempt to refresh credentials from active session

  let idToken;
  let diff = 0;

  try {
    idToken = window.localStorage.getItem(cognitoUserPoolId);
    if (idToken) diff = getRemainingSessionTime(idToken);
  } catch (error) {
    console.error(error);
  }

  if (diff > 0) {
    store.idToken = idToken;
    logoutTimer = setTimeout(logout, diff);
    setCredentials();
  } else {
    initApiGatewayClient(); // init a blank client (will get overwritten if we have creds)
    logout();
  }
}

/**
 * Gets triggered by the callback from the cognito user pool. Pretty much all it does is grab and store the idToken.
 */
export function login() {
  return new Promise((resolve, reject) => {
    let idToken;
    // let accessToken, username

    try {
      window.location.hash
        .replace(/^#/, "")
        .split("&")
        .map((param) => param.split("="))
        .forEach((param) => {
          // record the id_token and access_token
          if (param[0] === "id_token") idToken = param[1];
          // if (param[0] === 'access_token') accessToken = param[1]
        });

      if (idToken) {
        // we get both, we set both, but we only really care about the idToken
        // username = jwtDecode(idToken)['cognito:username']

        window.localStorage.setItem(cognitoUserPoolId, idToken);

        store.idToken = idToken;

        logoutTimer = setTimeout(logout, getRemainingSessionTime(idToken));

        setCredentials();

        resolve(idToken);
      }
    } catch (error) {
      reject(error);
    }
  });
}

export const getLoginRedirectUrl = () =>
  `${window.location.protocol}//${window.location.host}/index.html?action=login`;
export const getLogoutRedirectUrl = () =>
  `${window.location.protocol}//${window.location.host}/index.html?action=logout`;

function setCredentials() {
  const preferredRole = jwtDecode(store.idToken)["cognito:preferred_role"];
  const credentials = fromCognitoIdentityPool({
    client: new CognitoIdentityClient({ region: cognitoRegion }),
    identityPoolId: cognitoIdentityPoolId,
    logins: {
      [`cognito-idp.${cognitoRegion}.amazonaws.com/${cognitoUserPoolId}`]: store.idToken,
    },
    roleArn: preferredRole,
  });

  return credentials().then((creds) => {
    initApiGatewayClient(creds);
    store.user = {
      email: jwtDecode(store.idToken).email,
      UserId: jwtDecode(store.idToken).sub,
    };
    updateAllUserData();

    return apiGatewayClient()
      .then((apiGatewayClient) => apiGatewayClient.post("/signin", {}, {}, {}))
      .then((response) => {
        if (response.status === 200) {
          store.user.validWorkspace = response.data?.validWorkspace === true;
        }
      });
  }).catch((error) => {
    console.error(error);
    throw error;
  });
}

/**
 * Callback for the Cognito User Pool's logout just to make sure we clean up everything.
 */
export function logout() {
  clearTimeout(logoutTimer);
  logoutTimer = undefined;
  if (store.idToken) {
    store.resetUserData();
    window.localStorage.clear();

    if (cognitoDomain) {
      // redirect to cognito to log out there, too
      const redirectUrl = getLogoutRedirectUrl();
      window.location = `${cognitoDomain}/logout?client_id=${cognitoClientId}&logout_uri=${redirectUrl}`;
    }
  }
}